import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiChevronLeft, FiChevronRight, FiExternalLink, FiArrowRight } from 'react-icons/fi';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
  overflow-y: auto;
`;

const ModalContent = styled(motion.div)`
  background-color: var(--white);
  border-radius: var(--border-radius-md);
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  
  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr;
    max-height: 80vh;
  }
  
  .dark-mode & {
    background-color: var(--dark);
  }
`;

const ModalImageContainer = styled.div`
  position: relative;
  height: 300px;
  overflow: hidden;
  
  @media (min-width: 992px) {
    height: 100%;
  }
`;

const ModalImageWrapper = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
`;

const ModalImage = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  flex-shrink: 0;
`;

const ImageNavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.5);
  color: var(--white);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: var(--border-radius-circle);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all var(--transition-fast);
  
  &:hover {
    background-color: var(--secondary);
    color: var(--black);
  }
  
  &.prev {
    left: 1rem;
  }
  
  &.next {
    right: 1rem;
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  background-color: rgba(0, 0, 0, 0.7);
  color: var(--white);
  padding: 0.3rem 0.8rem;
  border-radius: var(--border-radius-sm);
  font-size: 0.9rem;
  z-index: 10;
`;

const ModalBody = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ModalCategory = styled.span`
  color: var(--white);
  font-size: 0.9rem;
  background-color: ${props => props.bgColor || 'var(--primary)'};
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  display: inline-block;
  margin-bottom: 1rem;
  width: fit-content;
`;

const ModalTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: var(--primary);
  
  .dark-mode & {
    color: var(--white);
  }
`;

const ModalDescription = styled.p`
  color: var(--gray-600);
  line-height: 1.8;
  margin-bottom: 2rem;
  
  .dark-mode & {
    color: var(--light);
    opacity: 0.8;
  }
`;

const ModalFeatures = styled.ul`
  list-style-type: none;
  margin-bottom: 1.5rem;
`;

const ModalFeature = styled.li`
  margin-bottom: 0.8rem;
  display: flex;
  align-items: flex-start;
  color: var(--dark);
  
  .dark-mode & {
    color: var(--light);
  }
  
  &::before {
    content: '';
    flex-shrink: 0;
    display: block;
    width: 8px;
    height: 8px;
    margin-top: 8px;
    margin-right: 10px;
    border-radius: 50%;
    background-color: ${props => props.color || 'var(--primary)'};
  }
`;

const ModalFooter = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1.5rem;
  border-top: 1px solid var(--gray-200);
  
  .dark-mode & {
    border-color: var(--gray-700);
  }
`;

const ModalLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary);
  font-weight: 600;
  transition: all var(--transition-fast);
  
  &:hover {
    color: var(--secondary);
    gap: 0.8rem;
  }
  
  svg {
    transition: transform var(--transition-fast);
  }
  
  &:hover svg {
    transform: translateX(3px);
  }
`;

const NoLinkText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--gray-500);
  font-weight: 500;
  font-style: italic;
  font-size: 0.9rem;
  
  .dark-mode & {
    color: var(--gray-400);
  }
`;

const ModalButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background-color: var(--primary);
  color: var(--white);
  padding: 0.8rem 1.5rem;
  border-radius: var(--border-radius-sm);
  font-weight: 600;
  text-decoration: none;
  transition: all var(--transition-fast);
  
  &:hover {
    background-color: var(--secondary);
    color: var(--black);
    transform: translateY(-3px);
    box-shadow: var(--shadow-md);
  }
  
  svg {
    transition: transform var(--transition-fast);
  }
  
  &:hover svg {
    transform: translateX(3px);
  }
`;

const ModalCloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: var(--white);
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 10;
  width: 40px;
  height: 40px;
  border-radius: var(--border-radius-circle);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  transition: all var(--transition-fast);
  
  &:hover {
    background-color: var(--secondary);
    color: var(--black);
    transform: rotate(90deg);
  }
`;

const BelgianAccent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 5px;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  .black, .yellow, .red {
    flex: 1;
  }
  
  .black {
    background-color: var(--black);
  }
  
  .yellow {
    background-color: var(--secondary);
  }
  
  .red {
    background-color: var(--accent);
  }
`;

const ProjectModal = ({ project, isOpen, onClose, categoryColors }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Direction pour l'animation des slides
  const [[page, direction], setPage] = useState([0, 0]);
  
  // Si project est null ou si le modal n'est pas ouvert, ne rien afficher
  if (!project || !isOpen) {
    return null;
  }
  
  // Simuler plusieurs images pour chaque projet
  const projectImages = [
    project.image,
    // Ajoutez des images supplémentaires si disponibles
    // Sinon, utilisez l'image principale avec différentes teintes
    project.image.replace('w=800', 'w=801'),
    project.image.replace('w=800', 'w=802')
  ];
  
  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === projectImages.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? projectImages.length - 1 : prevIndex - 1
    );
  };
  
  const getCategoryColor = () => {
    switch(project.category) {
      case 'site-vitrine':
        return categoryColors?.siteVitrine || 'var(--black)';
      case 'e-commerce':
        return categoryColors?.ecommerce || 'var(--secondary)';
      case 'application':
        return categoryColors?.application || 'var(--accent)';
      default:
        return 'var(--primary)';
    }
  };
  
  const getCategoryName = () => {
    switch(project.category) {
      case 'site-vitrine':
        return 'Site Vitrine';
      case 'e-commerce':
        return 'E-commerce';
      case 'application':
        return 'Application Web';
      default:
        return project.category;
    }
  };
  
  const modalVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, transition: { duration: 0.3 } }
  };
  
  const contentVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      transition: { 
        duration: 0.4,
        type: "spring",
        stiffness: 300,
        damping: 25
      } 
    },
    exit: { 
      opacity: 0, 
      scale: 0.8, 
      transition: { 
        duration: 0.3 
      } 
    }
  };
  
  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0
    })
  };
  
  const paginate = (newDirection) => {
    if (newDirection > 0) {
      nextImage();
    } else {
      prevImage();
    }
    setPage([page + newDirection, newDirection]);
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={onClose}
        >
          <ModalContent
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalCloseButton onClick={onClose}>
              <FiX />
            </ModalCloseButton>
            
            <ModalImageContainer>
              <AnimatePresence initial={false} custom={direction}>
                <ModalImageWrapper
                  key={currentImageIndex}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: "spring", stiffness: 300, damping: 30 },
                    opacity: { duration: 0.2 }
                  }}
                >
                  <ModalImage 
                    src={projectImages[currentImageIndex]} 
                    alt={project.title} 
                  />
                </ModalImageWrapper>
              </AnimatePresence>
              
              {projectImages.length > 1 && (
                <>
                  <ImageNavButton className="prev" onClick={() => paginate(-1)}>
                    <FiChevronLeft />
                  </ImageNavButton>
                  <ImageNavButton className="next" onClick={() => paginate(1)}>
                    <FiChevronRight />
                  </ImageNavButton>
                  <ImageCounter>
                    {currentImageIndex + 1} / {projectImages.length}
                  </ImageCounter>
                </>
              )}
            </ModalImageContainer>
            
            <ModalBody>
              <BelgianAccent>
                <div className="black"></div>
                <div className="yellow"></div>
                <div className="red"></div>
              </BelgianAccent>
              
              <ModalCategory bgColor={getCategoryColor()}>
                {getCategoryName()}
              </ModalCategory>
              <ModalTitle>{project.title}</ModalTitle>
              <ModalDescription>{project.description}</ModalDescription>
              
              <ModalFeatures>
                {project.features.map((feature, index) => (
                  <ModalFeature key={index} color={getCategoryColor()}>
                    {feature}
                  </ModalFeature>
                ))}
              </ModalFeatures>
              
              <ModalFooter>
                {project.link ? (
                  <ModalLink href={project.link} target="_blank" rel="noopener noreferrer">
                    Voir le site <FiExternalLink />
                  </ModalLink>
                ) : (
                  <NoLinkText>
                    Projet non publié en ligne
                  </NoLinkText>
                )}
                <ModalButton href="#contact">
                  Discuter d'un projet similaire <FiArrowRight />
                </ModalButton>
              </ModalFooter>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;
